import axios from 'axios';
import type {
    InvoicingSettings,
    InvoicingSettingsUpdatePayload,
    PricingPartKind,
    SupplierVendorNexAliasGroup,
} from '../hooks/useGetSettings';
import { cleanVendorNexAliasMappings } from './vendorNexAliasMappingsUtils';
import { findInvoicingSettingsSection } from '../pages/invoice/detail/invoiceDetailPageUtils';
import { resolveUnmatchedLineItemsNexSubmitMode } from './unmatchedLineItemsNexSubmitMode';

type SettingsSection = { key?: string; data?: unknown };

/** Full invoicing settings from a `/settings` response (includes markups, inbox, etc.). */
export function readFullInvoicingSettingsFromSections(
    topSections?: SettingsSection[],
    bottomSections?: SettingsSection[],
): InvoicingSettings {
    const section =
        findInvoicingSettingsSection(topSections) ?? findInvoicingSettingsSection(bottomSections);
    const data = section?.data as InvoicingSettings | undefined;
    return {
        freightAsLineItem: data?.freightAsLineItem ?? true,
        salesTaxAsLineItem: data?.salesTaxAsLineItem === true,
        vendorNexAliases: data?.vendorNexAliases ?? [],
        vendorNexAliasMappings: data?.vendorNexAliasMappings ?? [],
        flaggedSpecialVendors: data?.flaggedSpecialVendors ?? [],
        vendorFlaggingRules: data?.vendorFlaggingRules ?? [],
        emailInvoicingOptIn: data?.emailInvoicingOptIn ?? false,
        invoiceInboxBaseLocalPart: data?.invoiceInboxBaseLocalPart,
        invoiceInboxAddresses: data?.invoiceInboxAddresses,
        emailSetupGuideUrl: data?.emailSetupGuideUrl,
        inboundDefaultLocationId:
            data?.inboundDefaultLocationId != null
                ? String(data.inboundDefaultLocationId)
                : undefined,
        receivedAmountMismatchPercentThreshold: data?.receivedAmountMismatchPercentThreshold ?? 0.5,
        returnedAmountMismatchPercentThreshold: data?.returnedAmountMismatchPercentThreshold ?? 0.5,
        receivedAmountMismatchBaseDollarThreshold:
            data?.receivedAmountMismatchBaseDollarThreshold ?? 50,
        returnedAmountMismatchBaseDollarThreshold:
            data?.returnedAmountMismatchBaseDollarThreshold ?? 50,
        receivedAmountMismatchDollarThreshold:
            data?.receivedAmountMismatchDollarThreshold ??
            data?.receivedAmountMismatchBaseDollarThreshold ??
            50,
        returnedAmountMismatchDollarThreshold:
            data?.returnedAmountMismatchDollarThreshold ??
            data?.returnedAmountMismatchBaseDollarThreshold ??
            50,
        amountMismatchFlaggingEnabled: data?.amountMismatchFlaggingEnabled ?? true,
        amountMismatchFlagBothDirections: data?.amountMismatchFlagBothDirections !== false,
        quantityMismatchFlaggingEnabled: data?.quantityMismatchFlaggingEnabled ?? true,
        priceMismatchFlaggingEnabled: data?.priceMismatchFlaggingEnabled ?? true,
        nexLineQuantityBasis: data?.nexLineQuantityBasis === 'qtyOnOrder' ? 'qtyOnOrder' : 'qtyRec',
        nexReconciliationIdentifierMode: data?.nexReconciliationIdentifierMode ?? 'ro',
        defaultLkqMarkupPercentage: data?.defaultLkqMarkupPercentage,
        preferInvoiceListPriceOverLkqMarkup: data?.preferInvoiceListPriceOverLkqMarkup === true,
        defaultSubletMarkupPercentage: data?.defaultSubletMarkupPercentage,
        preferInvoiceListPriceOverSubletMarkup: data?.preferInvoiceListPriceOverSubletMarkup === true,
        lkqMarkups: data?.lkqMarkups ?? [],
        lkqDiscounts: data?.lkqDiscounts ?? [],
        subletMarkups: data?.subletMarkups ?? [],
        subletDiscounts: data?.subletDiscounts ?? [],
        discountFlagExpectations: data?.discountFlagExpectations ?? [],
        locationDefaultTaxRates: data?.locationDefaultTaxRates ?? [],
        unmatchedLineItemsNexSubmitMode: resolveUnmatchedLineItemsNexSubmitMode(data),
    };
}

/** The dimensions and grouping every pricing row shares; see below. */
interface PricingRowForApi {
    insuranceCompanyId: string;
    supplierId: string;
    state: string;
    partKind?: PricingPartKind;
    make?: string;
    insuranceGroupId?: string;
    parentInsuranceCompanyId?: string;
    parentInsuranceCompanyName?: string;
}

/**
 * What every pricing row sends: the dimensions it is keyed by, plus the
 * grouping that says which companies share one rule. Display names are dropped
 * — the endpoint stores ids — and each table adds its own percentage field.
 */
const pricingRowForApi = ({
    insuranceCompanyId,
    supplierId,
    state,
    partKind,
    make,
    insuranceGroupId,
    parentInsuranceCompanyId,
    parentInsuranceCompanyName,
}: PricingRowForApi) => ({
    insuranceCompanyId,
    supplierId,
    state,
    ...(partKind ? { partKind } : {}),
    ...(make ? { make } : {}),
    insuranceGroupId,
    parentInsuranceCompanyId,
    parentInsuranceCompanyName,
});

/** Shape expected by `POST /settings/update` (ids only, no display names). */
export function invoicingSettingsForApi(settings: InvoicingSettings): InvoicingSettingsUpdatePayload {
    const {
        flaggedSpecialVendors,
        vendorFlaggingRules,
        lkqMarkups,
        lkqDiscounts,
        subletMarkups,
        subletDiscounts,
        discountFlagExpectations,
        ...rest
    } = settings;
    return {
        ...rest,
        ...(flaggedSpecialVendors !== undefined && {
            flaggedSpecialVendors: flaggedSpecialVendors.map((vendor) => vendor.supplierId),
        }),
        ...(vendorFlaggingRules !== undefined && {
            vendorFlaggingRules: vendorFlaggingRules.map((rule) => {
                const { supplierName: _supplierName, ...rest } = rule;
                return rest;
            }),
        }),
        ...(lkqMarkups !== undefined && {
            lkqMarkups: lkqMarkups.map((row) => ({
                ...pricingRowForApi(row),
                markupPercentage: row.markupPercentage,
            })),
        }),
        ...(lkqDiscounts !== undefined && {
            lkqDiscounts: lkqDiscounts.map((row) => ({
                ...pricingRowForApi(row),
                discountPercentage: row.discountPercentage,
            })),
        }),
        ...(subletMarkups !== undefined && {
            subletMarkups: subletMarkups.map((row) => ({
                ...pricingRowForApi(row),
                markupPercentage: row.markupPercentage,
            })),
        }),
        ...(subletDiscounts !== undefined && {
            subletDiscounts: subletDiscounts.map((row) => ({
                ...pricingRowForApi(row),
                discountPercentage: row.discountPercentage,
            })),
        }),
        ...(discountFlagExpectations !== undefined && {
            discountFlagExpectations: discountFlagExpectations.map((row) => ({
                ...pricingRowForApi(row),
                expectedDiscountPercentage: row.expectedDiscountPercentage,
            })),
        }),
    };
}

/** Alias-only body. The settings editor still sends the full invoicing subdocument. */
export function buildVendorAliasMappingsOnlyPayload(
    mappings: SupplierVendorNexAliasGroup[],
): InvoicingSettingsUpdatePayload {
    return {
        vendorNexAliasMappings: cleanVendorNexAliasMappings(mappings),
    } as InvoicingSettingsUpdatePayload;
}

export function buildInvoicingSettingsPayloadForMappingsUpdate(
    current: InvoicingSettings,
    mappings: SupplierVendorNexAliasGroup[],
): InvoicingSettingsUpdatePayload {
    return invoicingSettingsForApi({
        ...current,
        vendorNexAliasMappings: cleanVendorNexAliasMappings(mappings),
    });
}

export function extractUpdateSettingsErrorMessage(error: unknown): string | undefined {
    if (!axios.isAxiosError(error)) return undefined;
    const data = error.response?.data;
    if (typeof data === 'object' && data !== null && 'message' in data) {
        const message = (data as { message?: unknown }).message;
        return typeof message === 'string' ? message : undefined;
    }
    if (error.response?.status === 403) {
        return 'Your account does not have permission to update organization settings.';
    }
    return undefined;
}
